<script setup lang="ts">
import type { Partner } from '~/composables/usePartners'
import { ref } from 'vue'
import { logApiError } from '~/utils/logger'

// SEO Configuration
const { setSeoMeta, getBreadcrumbSchema, getLocalBusinessSchema } = useSeo()

setSeoMeta({
  title: 'Partners van \'t SpeelFabrik - Samen Bouwen aan Speelruimte',
  description: 'Ontdek onze partners die mee bouwen aan het verhaal van \'t SpeelFabrik. Word ook partner en ondersteun onze missie om kinderen te laten ontdekken en groeien.',
  image: '/images/og-partners.jpg',
  keywords: ['partners', 'sponsoring', 'samenwerking', 'speelfabrik partners', 'word partner'],
})

// Branded OG image
defineOgImage('SpeelFabrik', {
  title: 'Partners',
  description: 'Ontdek onze partners die mee bouwen aan het verhaal van \'t SpeelFabrik.',
})

useSchemaOrg([
  getLocalBusinessSchema(),
  getBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Partners', path: '/partners' },
  ]),
  {
    '@type': 'WebPage',
    'name': 'Partners van \'t SpeelFabrik',
    'description': 'Onze partners die mee bouwen aan het verhaal van \'t SpeelFabrik',
    'url': 'https://www.tspeelfabrik.be/partners',
  },
])

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Partners van \'t SpeelFabrik',
    subtitle: 'Samen bouwen we aan meer speelruimte, creativiteit en mooie momenten voor kinderen',
    icon: 'bx-heart',
    iconColor: 'warning',
  },
})

// Contact form state
const name = ref('')
const email = ref('')
const phone = ref('')
const organization = ref('')
const message = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

// Submit contact form
const handleSubmit = async () => {
  // Reset messages
  successMessage.value = ''
  errorMessage.value = ''

  // Basic validation
  if (!name.value || !email.value || !message.value) {
    errorMessage.value = 'Vul alle verplichte velden in'

    return
  }

  isLoading.value = true

  try {
    const response = await $fetch('/api/contact/send', {
      method: 'POST',
      body: {
        name: name.value,
        email: email.value,
        phone: phone.value || undefined,
        message: `PARTNERSCHAP AANVRAAG\n\nOrganisatie: ${organization.value || 'Niet opgegeven'}\nTelefoon: ${phone.value || 'Niet opgegeven'}\n\n${message.value}`,
      },
    })

    // Show success message
    successMessage.value = response.message || 'Je bericht is succesvol verzonden! We nemen zo snel mogelijk contact met je op.'

    // Clear form
    name.value = ''
    email.value = ''
    phone.value = ''
    organization.value = ''
    message.value = ''
  }
  catch (error: unknown) {
    logApiError('/api/contact/send', error, { formType: 'partner' })

    const errorObj = error as { data?: { message?: string }, message?: string }

    errorMessage.value = errorObj.data?.message || errorObj.message || 'Er is iets misgegaan. Probeer het opnieuw.'
  }
  finally {
    isLoading.value = false
  }
}

// Fetch partners from database
const { getPartners } = usePartners()

const partners = ref<Partner[]>([])
const partnersLoading = ref(true)

// Fetch partners on mount
onMounted(async () => {
  try {
    const response = await getPartners({
      isActive: true,
      sortBy: 'display_order',
      sortOrder: 'asc',
      limit: 50,
    })

    partners.value = response.data
  }
  catch (error) {
    logApiError('/api/partners', error, { context: 'partners-page' })
  }
  finally {
    partnersLoading.value = false
  }
})
</script>

<template>
  <div class="partners-page">
    <VContainer>
      <!-- Introduction and Contact Form Section -->
      <VRow id="word-partner" class="mb-12">
        <!-- Information Column -->
        <VCol cols="12" md="6">
          <VCard>
            <VCardText class="text-center pa-8">
              <h2 class="text-h4 mb-4">
                Ondersteun 't SpeelFabrik
              </h2>
              <div class="text-body-1 text-medium-emphasis text-start">
                <p class="mb-4">
                  Bij 't SpeelFabrik geloven we sterk in samenwerking. Om onze speelse activiteiten te blijven uitbouwen
                  en vernieuwen, werken we samen met partners die onze missie en waarden mee ondersteunen. Dankzij deze
                  samenwerkingen kunnen we kinderen blijven laten ontdekken, creëren en groeien in een warme en veilige
                  omgeving.
                </p>

                <p class="mb-4">
                  Onze partners ondersteunen 't SpeelFabrik elk op hun eigen manier. Soms met materiaal of expertise,
                  soms
                  met een helpende hand of een fijn idee. Samen zorgen we voor meer speelruimte, creativiteit en mooie
                  momenten voor kinderen.
                </p>

                <p class="mb-4">
                  Heb je interesse om partner te worden van 't SpeelFabrik of wil je graag meer te weten komen over de
                  mogelijkheden? Dan nodigen we je graag uit om contact met ons op te nemen via het contactformulier. We
                  bekijken met plezier samen hoe een samenwerking vorm kan krijgen.
                </p>

                <p class="mb-0 font-weight-medium">
                  Samen bouwen aan meer speelruimte, creativiteit en mooie momenten voor kinderen. 💛
                </p>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Contact Form Column -->
        <VCol cols="12" md="6">
          <VCard>
            <VCardItem>
              <VCardTitle class="d-flex align-center gap-2">
                <VIcon icon="bx-envelope" size="24" />
                Word Partner
              </VCardTitle>
            </VCardItem>

            <VCardText>
              <p class="text-body-1 text-medium-emphasis mb-6">
                Ben je geïnteresseerd om partner te worden of wil je meer informatie? Vul het formulier in en we nemen
                zo snel mogelijk contact met je op.
              </p>

              <!-- Success Message -->
              <VAlert v-if="successMessage" type="success" closable class="mb-4" @click:close="successMessage = ''">
                {{ successMessage }}
              </VAlert>

              <!-- Error Message -->
              <VAlert v-if="errorMessage" type="error" closable class="mb-4" @click:close="errorMessage = ''">
                {{ errorMessage }}
              </VAlert>

              <VForm @submit.prevent="handleSubmit">
                <VTextField
                  v-model="name" label="Volledige naam *" :disabled="isLoading"
                  class="mb-4"
                />

                <VTextField
                  v-model="email" label="E-mailadres *" type="email"
                  :disabled="isLoading" class="mb-4"
                />

                <VTextField
                  v-model="phone" label="Telefoonnummer" type="tel"
                  :disabled="isLoading" class="mb-4"
                />

                <VTextField
                  v-model="organization" label="Organisatie"
                  :disabled="isLoading" class="mb-4"
                />

                <VTextarea
                  v-model="message"
                  rows="6"
                  label="Bericht *" :disabled="isLoading" class="mb-4"
                />

                <p class="text-caption text-medium-emphasis mb-4">
                  * Verplichte velden
                </p>

                <VBtn type="submit" color="primary" block :loading="isLoading" :disabled="isLoading">
                  <VIcon icon="bx-send" start />
                  Verstuur aanvraag
                </VBtn>
              </VForm>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Partners List Section -->
      <VRow>
        <VCol cols="12">
          <h2 class="text-h3 text-center mb-2">
            <VIcon icon="bx-trophy" size="32" class="me-2" />
            Onze Partners
          </h2>
          <p class="text-body-1 text-medium-emphasis text-center mb-8">
            Bedrijven en organisaties die mee bouwen aan het verhaal van 't SpeelFabrik
          </p>

          <!-- Loading State -->
          <div
            v-if="partnersLoading"
            class="text-center pa-8"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="48"
            />
            <p class="text-body-1 text-medium-emphasis mt-4">
              Partners laden...
            </p>
          </div>

          <!-- Partners Grid -->
          <VRow v-else-if="partners.length > 0">
            <VCol
              v-for="partner in partners"
              :key="partner.id"
              cols="12"
              sm="6"
              md="4"
            >
              <VCard
                class="h-100 partner-card"
                :href="partner.website || undefined"
                :target="partner.website ? '_blank' : undefined"
                :rel="partner.website ? 'noopener noreferrer' : undefined"
              >
                <VCardText class="d-flex flex-column align-center justify-center pa-6">
                  <VAvatar
                    size="100"
                    color="primary"
                    variant="tonal"
                    class="mb-4"
                  >
                    <VImg
                      v-if="partner.image_url"
                      :src="partner.image_url"
                      :alt="partner.name"
                      cover
                    />
                    <VIcon
                      v-else
                      icon="bx-building"
                      size="48"
                    />
                  </VAvatar>
                  <h4 class="text-h6 text-center">
                    {{ partner.name }}
                  </h4>
                  <p
                    v-if="partner.description"
                    class="text-body-2 text-center text-medium-emphasis mt-2"
                  >
                    {{ partner.description }}
                  </p>
                  <VChip
                    v-if="partner.website"
                    color="primary"
                    variant="outlined"
                    size="small"
                    class="mt-3"
                  >
                    <VIcon icon="bx-link-external" size="14" start />
                    Bezoek website
                  </VChip>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>

          <!-- Placeholder message when no partners -->
          <VCard v-else-if="!partnersLoading" class="pa-8 text-center">
            <VIcon icon="bx-heart" size="64" color="warning" class="mb-4" />
            <h3 class="text-h5 mb-4">
              Wij zijn op zoek naar partners!
            </h3>
            <p class="text-body-1 text-medium-emphasis mb-6">
              Ben jij of je organisatie geïnteresseerd om partner te worden van 't SpeelFabrik?<br>
              Vul het contactformulier hierboven in en ontdek de mogelijkheden.
            </p>
            <VBtn color="primary" size="large" href="#word-partner">
              <VIcon icon="bx-envelope" class="me-2" />
              Neem contact op
            </VBtn>
          </VCard>

          <!-- Call to action for more partners -->
          <VCard v-if="partners.length > 0 && !partnersLoading" class="mt-8 pa-6 text-center" color="primary" variant="tonal">
            <h3 class="text-h6 mb-2">
              Wil jij ook partner worden?
            </h3>
            <p class="text-body-2 mb-4">
              Sluit je aan bij onze partners en ondersteun 't SpeelFabrik!
            </p>
            <VBtn color="primary" href="#word-partner">
              <VIcon icon="bx-envelope" class="me-2" />
              Neem contact op
            </VBtn>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.partners-page {
  min-block-size: 60vh;
}

.partner-card {
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 10%);
    transform: translateY(-4px);
  }
}
</style>
